import classNames from "classnames/bind";
import { filter } from "es-toolkit/compat";
import React, { useMemo } from "react";
import FontAwesome from "react-fontawesome";
import Select, {
  components,
  createFilter,
  GroupBase,
  StylesConfig,
} from "react-select";
import styles from "../../../constants/index";
import FormInputWrapper from "../FormInputWrapper";
import cssStyles from "./SelectInput.module.scss";
import type {
  ControlledSelectInputProps,
  DropdownIndicatorExtendedProps,
  OptionType,
  SelectInputProps,
} from "./types";

const cx = classNames.bind(cssStyles);

const DropdownIndicator = (props: DropdownIndicatorExtendedProps) => {
  const { isValidated, isDisabled, isError } = props.selectProps;

  return (
    <components.DropdownIndicator {...props}>
      {isValidated && !isDisabled && isError ? (
        <div className={cx("icon", "icon--error")}>
          <FontAwesome name="exclamation-circle" />
        </div>
      ) : (
        <div className={cx("icon", { "icon--disabled": isDisabled })}>
          <FontAwesome name="check" />
        </div>
      )}
    </components.DropdownIndicator>
  );
};

function getOptionsWithLabels(options: any, labelKey: string) {
  if (labelKey) {
    return options.map((option: any) => {
      option.label = option[labelKey];
      option.value = option[labelKey];
      return option;
    });
  } else {
    return filter(options, (option: any) => option).map((option: any) => ({
      label: option,
      value: option,
    }));
  }
}

const customStyles: StylesConfig<unknown, boolean, GroupBase<any>> = {
  container: (provided) => ({
    ...provided,
    minWidth: 0,
    flex: 1,
  }),
  control: (provided, state) => {
    // @ts-expect-error unreachable type
    const hasError = state?.selectProps?.isError;

    let borderStyles = { border: `1px solid ${styles.surface["100"]}` };

    if (state.isDisabled) {
      borderStyles = { border: `1px solid ${styles.surface["200"]}` };
    } else if (hasError) {
      borderStyles = { border: `1px solid ${styles.status.error}` };
    } else if (state.isFocused) {
      borderStyles = { border: `1px solid ${styles.navy["500"]}` };
    }

    const paddingStyles = state.isMulti
      ? { padding: "3px 16px", minHeight: "36px" }
      : { padding: "5px 16px" };

    return {
      ...provided,
      minWidth: "120px",
      cursor: "pointer",
      borderRadius: "6px",
      ...paddingStyles,
      color: state.isDisabled ? styles.text["400"] : `${styles.text["800"]}`,
      fontSize: "14px",
      minHeight: "36px",
      lineHeight: "24px",
      letterSpacing: "0.01em",
      caretColor: hasError ? styles.status.error : `${styles.navy["500"]}`,
      boxShadow: "none",
      "&:hover": {
        border: hasError
          ? `1px solid ${styles.status.error}`
          : `1px solid ${styles.navy["500"]}`,
      },
      ...borderStyles,
    };
  },
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  placeholder: (provided) => ({ ...provided, margin: 0 }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: 0,
    fill: styles.surface["800"],
    stroke: styles.surface["800"],
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    margin: "6px 0px",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: 0,
    paddingLeft: "8px",
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: 0,
    paddingRight: "8px",
  }),
  loadingIndicator: (provided) => ({
    ...provided,
    padding: 0,
    paddingRight: "8px",
  }),
  multiValue: (provided, state) => ({
    ...provided,
    backgroundColor: state.isDisabled
      ? styles.surface["75"]
      : styles.surface["100"],
  }),
  multiValueLabel: (provided, state) => ({
    ...provided,
    color: state.isDisabled ? styles.text["400"] : styles.text["800"],
    fontSize: "14px",
    lineHeight: "24px",
    letterSpacing: "0.01em",
    padding: 0,
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    "&:hover": { backgroundColor: styles.status.errorLight },
  }),
  option: (provided, state) => ({
    ...provided,
    cursor: "pointer",
    backgroundColor: state.isSelected
      ? styles.navy["100"]
      : state.isFocused
        ? styles.navy["50"]
        : "#fff",
    ":active": {
      backgroundColor: styles.navy["100"],
    },
    padding: "8px 12px",
  }),
  menu: (provided) => ({
    ...provided,
    marginTop: "12px",
    zIndex: styles.zIndexPopover,
  }),
};

/**
 * @deprecated use "Dropdown" instead (see Storybook documentation)
 */
export const SelectInput = ({
  options,
  labelKey,
  onChange,
  isMulti,
  value,
  onBlur,
  ignoreCase,
  showIcon,
  isValidated,
  isDisabled,
  isLoading,
  closeMenuOnSelect,
  placeholder,
  isClearable,
  name,
  inputRef,
  error,
  classNamePrefix,
  unit,
}: SelectInputProps) => {
  const option = useMemo(
    () => getOptionsWithLabels(options, labelKey),
    [options, labelKey]
  );

  const onMultipleSelectChange = (selectedValues: any) => {
    if (!labelKey) {
      selectedValues =
        selectedValues?.map((value: OptionType) => value.label) ?? [];
    }

    onChange?.(selectedValues ?? []);
  };

  const onSelectChange = (selectedValue: any) => {
    if (!labelKey) {
      selectedValue = selectedValue?.value;
    }

    onChange?.(selectedValue);
  };

  const selectValue = value ? (isMulti ? value : [value]) : [];

  return (
    <div className={cx("container")}>
      <Select
        styles={customStyles}
        value={getOptionsWithLabels(selectValue, labelKey)}
        options={option}
        classNamePrefix={classNamePrefix}
        onChange={(selectedValue) =>
          isMulti
            ? onMultipleSelectChange(selectedValue)
            : onSelectChange(selectedValue)
        }
        onBlur={onBlur}
        isMulti={isMulti}
        filterOption={createFilter({
          ignoreAccents: false,
          ignoreCase: ignoreCase,
        })}
        // @ts-expect-error unreachable type
        components={showIcon && isValidated ? { DropdownIndicator } : undefined}
        isDisabled={isDisabled}
        isLoading={isLoading}
        closeMenuOnSelect={closeMenuOnSelect}
        placeholder={placeholder}
        isClearable={isClearable}
        inputId={name}
        // @ts-expect-error: it's not typed as the component wants but the
        // library doesn't do a good job of exporting types so we're choosing
        // not to use it
        ref={inputRef}
        // extra props to pass to custom components
        isValidated={isValidated}
        isError={error}
      />

      {unit && <div className={cx("unit")}>{unit}</div>}
    </div>
  );
};

/**
 * @deprecated use "Dropdown" instead (see Storybook documentation)
 */
const ControlledSelectInput = (props: ControlledSelectInputProps) => {
  return (
    <FormInputWrapper
      {...props}
      as={SelectInput}
    />
  );
};

export default ControlledSelectInput;
